import * as _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import ColorStack from '../utils/ColorStack';
import {
    IRootStoreExporter,
    IColorsStackSerializedValuesData,
} from '../interfaces';
import KeyValueStore from './KeyValueStore';
import {
    IActiveCountryAPI,
    IWithCountryProps,
} from 'services/APIService';
import { sortCountriesByName } from '../utils/countryUtils';
import { drop, first, xor } from 'lodash/fp';
export type ICountryStoreSerializedColors = IColorsStackSerializedValuesData<
    number
>;


export class CountryStore implements IRootStoreExporter {
    public colorStack: ColorStack<number>;
    public currentHoveredCountry: number = null;
    public currentStore: KeyValueStore<number> = new KeyValueStore(
        true,
        Number
    );
    public countries: IWithCountryProps = null;

    private defaultColorStack: ColorStack<number>;
    constructor() {
        makeAutoObservable(this);
        this.defaultColorStack = new ColorStack<number>();
        this.colorStack = this.defaultColorStack;
    }

    public serializeColors(): ICountryStoreSerializedColors[] {
        return this.colorStack.serialize();
    }

    public setStacks(colorStack: ColorStack<number>) {
        this.colorStack = colorStack;
    }

    public setDefaultStacks() {
        this.colorStack = this.defaultColorStack;
    }

    public toInitialState() {
        this.clear(true);
    }

    updateCountries({ countries, licensed, all }: IActiveCountryAPI) {
        this.countries = {
            countries,
            countriesArr: sortCountriesByName(_.values(countries)),
            licensedCountries: licensed,
            licensedCountriesArr: sortCountriesByName(_.values(licensed)),
            allCountries: all,
            allCountriesArr: sortCountriesByName(_.values(all)),
        };
    }

    public toggleIsMultiValue(override?: boolean) {
        if (this.currentStore) {
            this.currentStore.toggleIsMultiValue(override);
        }
    }

    public get currentCountryList(): number[] {
        if (!this.currentStore) {
            return [];
        }
        return this.currentStore.getValues();
    }

    public addCountry(countryID: number, ...ids: number[]) {
        if (this.currentStore === null) {
            return;
        }
        this.currentStore.setValue(countryID, ...ids);
    }

    public removeCountry(countryID: number, ...ids: number[]) {
        if (this.currentStore === null) {
            return;
        }
        this.currentStore.removeValue(countryID, ...ids);
    }

    public toggleCountry(countryID: number, ...ids: number[]) {
        if (this.currentStore === null) {
            return;
        }

        this.currentStore.toggleValue(countryID, ...ids);
    }

    public toggleSecondaryCountry(countryID: number) {
        const newCountryList = [
            first(this.currentCountryList),
            ...xor([countryID], drop(1, this.currentCountryList)),
        ];
        this.currentStore.overrideValues(...newCountryList);
    }

    public changeFirstCountry(countryID: number, ...ids: number[]) {
        if (this.currentStore === null) {
            return;
        }
        // we can simply assign the new value as the first country, since the second one will be reseted (removed);
        this.currentStore.clean();

        this.currentStore.toggleValue(countryID);
    }

    public clear(all: boolean = false) {
        if (this.currentStore) {
            this.currentStore.clean();
        }
    }

    public setHoveredCountry(countryID: number) {
        this.currentHoveredCountry = countryID;
    }

    public get lastFromStack() {
        const list = this.currentCountryList;
        return _.last(list);
    }

    get isMultiValue(): boolean {
        return this.currentStore ? this.currentStore.isMultiValue : false;
    }

    public customExport() {
        return {
            selectedCountries: this.currentCountryList.map(id => {
                return {
                    id,
                    color: this.colorStack.color(id),
                };
            }),
        };
    }

    public customRestore(data: {
        selectedCountries: Array<{ id: number; color: string }>;
    }) {
        this.clear(false);
        _.forEach(data.selectedCountries, ({ id }) => {
            this.addCountry(id);
        });
    }
}

export default new CountryStore();
